import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Lightbulb, Heart, Users, Zap, Target, Sparkles } from 'lucide-react';

type InsightType = 'trend_up' | 'trend_down' | 'discovery' | 'connection' | 'pattern' | 'goal' | 'energy';

interface InsightCardProps {
  type: InsightType;
  title: string;
  description: string;
  metric?: {
    value: number;
    label: string;
    change?: number;
  };
  delay?: number;
  onClick?: () => void;
}

const insightConfig: Record<InsightType, {
  icon: typeof TrendingUp;
  gradient: string;
  iconBg: string;
}> = {
  trend_up: {
    icon: TrendingUp,
    gradient: 'from-emerald-500/10 to-teal-500/5',
    iconBg: 'bg-emerald-500'
  },
  trend_down: {
    icon: TrendingDown,
    gradient: 'from-rose-500/10 to-orange-500/5',
    iconBg: 'bg-rose-500'
  },
  discovery: {
    icon: Lightbulb,
    gradient: 'from-amber-500/10 to-yellow-500/5',
    iconBg: 'bg-amber-500'
  },
  connection: {
    icon: Users,
    gradient: 'from-blue-500/10 to-indigo-500/5',
    iconBg: 'bg-blue-500'
  },
  pattern: {
    icon: Sparkles,
    gradient: 'from-purple-500/10 to-violet-500/5',
    iconBg: 'bg-purple-500'
  },
  goal: {
    icon: Target,
    gradient: 'from-indigo-500/10 to-purple-500/5',
    iconBg: 'bg-indigo-500'
  },
  energy: {
    icon: Zap,
    gradient: 'from-orange-500/10 to-amber-500/5',
    iconBg: 'bg-orange-500'
  }
};

const InsightCard: React.FC<InsightCardProps> = ({
  type,
  title,
  description,
  metric,
  delay = 0,
  onClick
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const config = insightConfig[type];
  const Icon = config.icon;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`
        group relative overflow-hidden rounded-[24px] lg:rounded-[28px] xl:rounded-[32px] p-4 lg:p-6 xl:p-7
        bg-gradient-to-br ${config.gradient}
        border border-black/5
        transition-all duration-700 ease-out
        hover:shadow-lg hover:scale-[1.02] cursor-pointer
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      onClick={onClick}
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-white/20 to-transparent rounded-full blur-3xl transform translate-x-16 -translate-y-16 group-hover:scale-150 transition-transform duration-700" />

      <div className="relative z-10">
        <div className="flex items-start gap-3 lg:gap-4">
          <div className={`w-10 h-10 lg:w-12 lg:h-12 rounded-xl lg:rounded-2xl ${config.iconBg} flex items-center justify-center shrink-0 shadow-lg`}>
            <Icon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
          </div>

          <div className="flex-1 min-w-0">
            <h4 className="text-[15px] lg:text-[17px] xl:text-[18px] font-bold text-black mb-1 lg:mb-1.5 leading-tight">{title}</h4>
            <p className="text-[13px] lg:text-[14px] xl:text-[15px] text-black/60 leading-relaxed">{description}</p>
          </div>
        </div>

        {metric && (
          <div className="mt-4 lg:mt-5 pt-4 lg:pt-5 border-t border-black/5">
            <p className="text-[10px] lg:text-[11px] font-bold text-black/40 uppercase tracking-wider text-center mb-2">{metric.label}</p>
            <div className="flex items-center justify-center gap-3">
              <p className="text-[32px] lg:text-[36px] xl:text-[40px] font-black text-black leading-none">{metric.value}</p>
              {metric.change !== undefined && (
                <div className={`flex items-center gap-1 ${metric.change >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                  {metric.change >= 0 ? <TrendingUp size={14} className="lg:w-4 lg:h-4" /> : <TrendingDown size={14} className="lg:w-4 lg:h-4" />}
                  <span className="text-[12px] lg:text-[13px] font-bold">
                    {metric.change >= 0 ? '+' : ''}{metric.change}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsightCard;
